<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CloseIcon } from '@solar-icons/vue/linear'
import AppTooltip from '../../primitives/AppTooltip/AppTooltip.vue'
import { CONTEXT_KIND_ICONS, excerptLead, type ContextItem } from './context-types'

const props = defineProps<{
  item: ContextItem
}>()

const emit = defineEmits<{ remove: [id: string] }>()

const { t } = useI18n()

const isScreen = computed(() => props.item.kind === 'section')
const removable = computed(() => !isScreen.value && !props.item.locked)
const passage = computed(() =>
  props.item.excerpt ? t('chat.context.passage', { text: excerptLead(props.item.excerpt) }) : '',
)
</script>

<template>
  <AppTooltip :content="item.excerpt ?? ''" :open="item.excerpt ? undefined : false">
    <template #trigger="{ aria }">
      <span
        v-bind="item.excerpt ? aria : {}"
        :class="[
          'icon-xs inline-flex h-12 min-w-0 max-w-full items-center gap-2 rounded-full pl-4 text-caption text-text-primary',
          removable ? 'pr-2' : 'pr-4',
          isScreen ? 'shrink-0 bg-bg-surface-accent' : 'shrink bg-bg-surface',
        ]"
      >
        <component
          :is="CONTEXT_KIND_ICONS[item.kind]"
          aria-hidden="true"
          :class="['shrink-0', isScreen ? 'text-accent-blue' : 'text-text-secondary']"
        />
        <span class="min-w-0 truncate"
          ><span class="text-caption-medium">{{ item.label }}</span
          ><span v-if="passage" class="ml-2 text-text-secondary">{{ passage }}</span></span
        >
        <button
          v-if="removable"
          type="button"
          :aria-label="t('chat.removeContextItem', { label: item.label })"
          class="grid size-8 shrink-0 place-items-center rounded-full text-text-tertiary outline-none transition-colors duration-fast ease-soft hover:bg-bg-wash hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue motion-reduce:transition-none"
          @click="emit('remove', item.id)"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </span>
    </template>
  </AppTooltip>
</template>
